import styled, { css } from 'styled-components';
import { mutedStyle } from './typography';

const paddings = {
  none: '0',
  sm: '16px',
  default: '24px',
  lg: '32px',
};

/**
 * Surface for grouped content.
 *
 *   <Card>…</Card>
 *   <Card $padding="none">…</Card>
 */
export const Card = styled.div`
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  color: var(--card-foreground);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: ${({ $padding }) => paddings[$padding] || paddings.default};
  position: relative;
`;

const lift = css`
  transition:
    transform 500ms ease-in-out,
    box-shadow 500ms ease-in-out,
    border-color 500ms ease-in-out;

  &:hover,
  &:focus-within {
    border-color: color-mix(in oklab, var(--primary) 50%, transparent);
    box-shadow:
      0 20px 25px -5px color-mix(in oklab, var(--primary) 10%, transparent),
      0 8px 10px -6px color-mix(in oklab, var(--primary) 10%, transparent);
    transform: translateY(-6px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:focus-within {
      transform: none;
    }
  }
`;

/**
 * Marketing feature card. Lifts and glows on hover when `$interactive` is set
 * (use it for cards that link somewhere). To make the whole card clickable,
 * put a <CardLink> around the title.
 */
export const FeatureCard = styled(Card)`
  height: 100%;

  ${({ $interactive }) => $interactive && lift};
`;

/** A link whose hit area stretches over the nearest Card. */
export const CardLink = styled.a`
  color: inherit;
  text-decoration: none;

  &::after {
    content: '';
    inset: 0;
    position: absolute;
  }

  /* Move the focus ring from the text to the whole card. */
  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    border-radius: var(--radius-2xl);
    outline: 2px solid var(--ring);
    outline-offset: -2px;
  }
`;

export const CardDescription = styled.p`
  ${mutedStyle};
  font-size: 16px;
  line-height: 24px;
  margin-top: 8px;
`;
